import React from "react";
import { X, CheckCircle, Receipt } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CommissionAPI } from "@/api/type";
import dayjs from "dayjs";
import { formatCurrency } from "@/lib/commonConvert";

interface InvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  commission: CommissionAPI | null;
  bankInfo?: {
    bankName: string;
    accountNumber: string;
    accountHolder: string;
  } | null;
  userInfo?: {
    fullname: string;
    email: string;
    phone: string;
    referralCode?: string;
  } | null;
}

const InvoiceModal: React.FC<InvoiceModalProps> = ({
  isOpen,
  onClose,
  commission,
  bankInfo,
  userInfo,
}) => {
  const { t } = useTranslation();

  if (!isOpen || !commission) return null;

  const invoiceNumber = `HD-${dayjs(commission.payable_at).format("YYYYMM")}-${String(commission.id).padStart(3, "0")}`;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "paid":
        return (
          <span className="flex items-center gap-1.5 !px-3 !py-1.5 !bg-green-50 dark:!bg-green-900/30 !text-green-700 dark:!text-green-300 !rounded-full !text-sm !font-medium border border-green-200 dark:border-green-800">
            <CheckCircle className="w-4 h-4" />
            {t("payment.invoice.status_paid")}
          </span>
        );
      case "approved":
        return (
          <span className="!px-3 !py-1.5 !bg-blue-50 dark:!bg-blue-900/30 !text-blue-700 dark:!text-blue-300 !rounded-full !text-sm !font-medium border border-blue-200 dark:border-blue-800">
            {t("payment.invoice.status_approved")}
          </span>
        );
      case "pending":
        return (
          <span className="!px-3 !py-1.5 !bg-yellow-50 dark:!bg-yellow-900/30 !text-yellow-700 dark:!text-yellow-300 !rounded-full !text-sm !font-medium border border-yellow-200 dark:border-yellow-800">
            {t("payment.invoice.status_pending")}
          </span>
        );
      case "rejected":
        return (
          <span className="!px-3 !py-1.5 !bg-red-50 dark:!bg-red-900/30 !text-red-700 dark:!text-red-300 !rounded-full !text-sm !font-medium border border-red-200 dark:border-red-800">
            {t("payment.invoice.status_rejected")}
          </span>
        );
      default:
        return (
          <span className="!px-3 !py-1.5 !bg-purple-50 dark:!bg-purple-900/30 !text-purple-700 dark:!text-purple-300 !rounded-full !text-sm !font-medium border border-purple-200 dark:border-purple-800">
            {t("payment.invoice.status_waiting")}
          </span>
        );
    }
  };

  return (
    <div className="fixed inset-0 !bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="!bg-white dark:!bg-gray-800 rounded-lg w-full max-w-4xl mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="!w-10 !h-10 !bg-blue-600 rounded-md flex items-center justify-center">
              <Receipt className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="!text-xl !font-bold !text-blue-600 dark:!text-blue-400">
                {t("payment.invoice.title")}
              </h2>
              <p className="!text-sm !text-gray-500 dark:!text-gray-400">
                {t("payment.invoice.invoice_number")}: {invoiceNumber}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {getStatusBadge(commission.status)}
            <button
              onClick={onClose}
              className="!bg-transparent !border-none !text-gray-400 hover:!text-gray-600 dark:hover:!text-gray-300 !p-1"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Payment Unit */}
            <div className="!bg-gray-50 dark:!bg-gray-700/50 rounded- p-4">
              <p className="!text-xs !font-medium !text-gray-500 dark:!text-gray-400 uppercase tracking-wide mb-3">
                {t("payment.invoice.payment_unit")}
              </p>
              <div className="flex items-start gap-3">
                <div>
                  <p className="!text-sm !font-bold !text-gray-900 dark:!text-white">
                    CTY TNHH XXX
                  </p>
                  <p className="!text-xs !text-gray-500 dark:!text-gray-400 mt-1">
                    {t("payment.invoice.address")}: 123 Đường Số 1, Quận 1, TP.
                    Hồ Chí Minh
                  </p>
                  <p className="!text-xs !text-gray-500 dark:!text-gray-400">
                    {t("payment.invoice.phone")}: (028) 3888 9999
                  </p>
                  <p className="!text-xs !text-gray-500 dark:!text-gray-400">
                    Email: contact@beq.com
                  </p>
                </div>
              </div>
            </div>

            {/* Collaborator Info */}
            <div className="!bg-gray-50 dark:!bg-gray-700/50 rounded-md p-4">
              <p className="!text-xs !font-medium !text-gray-500 dark:!text-gray-400 uppercase tracking-wide mb-3">
                {t("payment.invoice.collaborator")}
              </p>
              <div>
                <p className="!text-lg !font-bold !text-gray-900 dark:!text-white">
                  {userInfo?.fullname ||
                    commission.user_info?.fullname ||
                    "N/A"}
                </p>
                <p className="!text-xs !text-gray-500 dark:!text-gray-400 mt-1">
                  {t("payment.invoice.ctv_code")}: CTV-{commission.user_id}
                </p>
                <p className="!text-xs !text-gray-500 dark:!text-gray-400">
                  Email:{" "}
                  {userInfo?.email || commission.user_info?.email || "N/A"}
                </p>
                <p className="!text-xs !text-gray-500 dark:!text-gray-400">
                  {t("payment.invoice.phone")}:{" "}
                  {userInfo?.phone || commission.user_info?.phone || "N/A"}
                </p>
              </div>
            </div>
          </div>

          {bankInfo && (
            <div className="flex items-center gap-4 p-4 !bg-blue-50 dark:!bg-blue-900/20 rounded-md mb-6 border border-blue-100 dark:border-blue-800">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <div className="flex flex-col gap-1">
                  <span className="!text-xs !font-medium !text-gray-500 dark:!text-gray-400 uppercase">
                    {t("payment.invoice.bank_info")}
                  </span>
                  <span className="!text-sm !font-semibold !text-gray-900 dark:!text-white">
                    {bankInfo.bankName} - {bankInfo.accountNumber}
                  </span>
                </div>
              </div>
              <div className="flex-1"></div>
              <div className="text-right">
                <span className="!text-xs !text-gray-500 dark:!text-gray-400 uppercase">
                  {t("payment.invoice.account_holder")}
                </span>
                <p className="!text-sm !font-semibold !text-gray-900 dark:!text-white">
                  {bankInfo.accountHolder}
                </p>
              </div>
            </div>
          )}

          {/* Commission Details Table */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden mb-6 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="!bg-gray-50 dark:!bg-gray-700">
                  <th className="px-4 py-3 !text-xs !font-medium !text-gray-500 dark:!text-gray-400 uppercase text-center">
                    {t("payment.invoice.stt")}
                  </th>
                  <th className="px-4 py-3 !text-xs !font-medium !text-gray-500 dark:!text-gray-400 uppercase text-center">
                    {t("payment.invoice.order_date")}
                  </th>
                  <th className="px-4 py-3 !text-xs !font-medium !text-gray-500 dark:!text-gray-400 uppercase text-center">
                    {t("payment.invoice.order_code")}
                  </th>
                  <th className="px-4 py-3 !text-xs !font-medium !text-gray-500 dark:!text-gray-400 uppercase text-left">
                    {t("payment.invoice.customer")}
                  </th>
                  <th className="px-4 py-3 !text-xs !font-medium !text-gray-500 dark:!text-gray-400 uppercase text-right">
                    {t("payment.invoice.revenue")}
                  </th>
                  <th className="px-4 py-3 !text-xs !font-medium !text-gray-500 dark:!text-gray-400 uppercase text-center">
                    {t("payment.invoice.rate")}
                  </th>
                  <th className="px-4 py-3 !text-xs !font-medium !text-gray-500 dark:!text-gray-400 uppercase text-right">
                    {t("payment.invoice.commission")}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-gray-200 dark:border-gray-700">
                  <td className="px-4 py-3 !text-sm !text-gray-700 dark:!text-gray-300 text-center">
                    1
                  </td>
                  <td className="px-4 py-3 !text-sm !text-gray-700 dark:!text-gray-300 text-center whitespace-nowrap">
                    {dayjs(commission.payable_at).format("YYYY-MM-DD")}
                  </td>
                  <td className="px-4 py-3 !text-sm !text-gray-900 dark:!text-white !font-medium text-center whitespace-nowrap">
                    ORD-P-{String(commission.transaction_id).padStart(3, "0")}
                  </td>
                  <td className="px-4 py-3 !text-sm !text-gray-700 dark:!text-gray-300 text-left whitespace-nowrap">
                    {commission.user_info?.fullname || "N/A"}
                  </td>
                  <td className="px-4 py-3 !text-sm !text-gray-700 dark:!text-gray-300 text-right whitespace-nowrap">
                    {formatCurrency(
                      Number(commission.gross_amount) /
                        (commission.commission_rate_percent / 100),
                      "đ",
                    )}
                  </td>
                  <td className="px-4 py-3 !text-sm !text-gray-700 dark:!text-gray-300 text-center">
                    {commission.commission_rate_percent}%
                  </td>
                  <td className="px-4 py-3 !text-sm !text-blue-600 dark:!text-blue-400 !font-medium text-right">
                    {formatCurrency(Number(commission.gross_amount), "đ")}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Notes and Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="!bg-gray-50 dark:!bg-gray-700/50 rounded-md p-4">
              <p className="!text-xs !font-medium !text-gray-500 dark:!text-gray-400 uppercase tracking-wide mb-2">
                {t("payment.invoice.notes")}
              </p>
              <p className="!text-sm !text-gray-700 dark:!text-gray-300">
                {commission.description}
              </p>
            </div>

            <div className="!bg-gray-50 dark:!bg-gray-700/50 rounded-md p-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="!text-sm !text-gray-600 dark:!text-gray-400">
                    {t("payment.invoice.total_commission_before_tax")}:
                  </span>
                  <span className="!text-sm !font-medium !text-gray-900 dark:!text-white">
                    {formatCurrency(Number(commission.gross_amount), "đ")}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="!text-sm !text-gray-600 dark:!text-gray-400">
                    {t("payment.invoice.tax_deduction")} (
                    {commission.commission_rate_percent > 0 ? "10" : "0"}%):
                  </span>
                  <span className="!text-sm !font-medium !text-red-600 dark:!text-red-400">
                    - {formatCurrency(Number(commission.tax_fee), "đ")}
                  </span>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-600 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="!text-base !font-bold !text-gray-900 dark:!text-white">
                      {t("payment.invoice.total_received")}:
                    </span>
                    <span className="!text-xl !font-bold !text-green-600 dark:!text-green-400">
                      {formatCurrency(Number(commission.net_amount), "đ")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="!px-6 !py-2.5 !bg-gray-100 dark:!bg-gray-700 !text-gray-700 dark:!text-gray-300 rounded-md hover:!bg-gray-200 dark:hover:!bg-gray-600 !transition-colors !border-none !font-medium"
          >
            {t("common.close")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceModal;
